<!--
  The view selector and this view's filters share one row, wrapping onto
  further rows only when the window is too narrow to fit them side by side.
  Rendered outside the guards below so the selector is still available when
  the list itself cannot load.
-->
<div class="d-flex flex-wrap align-items-center mt-3 mb-3"
     style="gap: 8px 24px;">
  <app-reservations-view-toggle *ngIf="customerId"
                                [customerId]="customerId"></app-reservations-view-toggle>
  <!--
    The checkboxes are one flex item, not two, so that when the row is too
    narrow to hold them side by side the second wraps underneath the FIRST
    rather than back to the far left under the selector.
  -->
  <div class="d-flex flex-wrap" style="gap: 4px 24px; flex: 1 1 0; min-width: 240px;"
       *ngIf="!customerNotFound && (serverError === null)">
    <div class="form-check mb-0">
      <input class="form-check-input" type="checkbox"
             [(ngModel)]="showRosterBookings"
             (ngModelChange)="onFiltersChange()"
             name="showRosterBookings"
             id="showRosterBookingsCustomer">
      <label class="form-check-label" for="showRosterBookingsCustomer">
        Show bookings generated by a roster membership
      </label>
    </div>
    <div class="form-check mb-0">
      <input class="form-check-input" type="checkbox"
             [(ngModel)]="showNonpaymentCancelledBookings"
             (ngModelChange)="onFiltersChange()"
             name="showNonpaymentCancelledBookings"
             id="showNonpaymentCancelledBookingsCustomer">
      <label class="form-check-label"
             for="showNonpaymentCancelledBookingsCustomer">
        Show bookings which were cancelled due to non-payment
      </label>
    </div>
  </div>
</div>

<div *ngIf="customerNotFound">
  <h2>Customer not found.</h2>
</div>

<div *ngIf="serverError !== null">
  <h2>Server Error: {{ serverError }}</h2>
</div>

<div *ngIf="!customerNotFound && (serverError === null)">
  <div class="rounded-table-wrapper rounded-table-tight table-responsive">
    <table class="table table-12px">

      <thead>
      <tr>
        <th scope="col" colspan="99" class="text-primary" style="font-size: 16px; height: 39px;">
          <span style="position: relative; top: -2px;">Event Bookings</span>
        </th>
      </tr>
      <tr>
        <th scope="col">Booked</th>
        <th scope="col">Event</th>
        <th scope="col">Event Date</th>
        <th scope="col">Participants</th>
        <th scope="col">Payment</th>
        <th scope="col">Amount Due</th>
        <th scope="col"></th>
      </tr>
      </thead>

      <tbody *ngIf="!loadingBookings && bookingsList">
      <tr *ngFor="let booking of bookingsList.results"
          [class.booking-cancelled]="booking.is_cancelled">
        <td>
          <span class="text-nowrap">
            {{ dateStringFromISO8601(booking.created_local_date) }}
          </span>
          <!--
            Who made the booking, when that is not the customer whose profile
            this is — the parent-books-for-child case this view exists for.
          -->
          <div *ngIf="!booking.is_booked_by_this_customer && booking.booked_by"
               class="text-muted">
            by
            <!-- The booker's own bookings list: this booking is on it. -->
            <app-customer-link
              [customerId]="booking.booked_by.customer_id"
              [firstName]="booking.booked_by.first_name"
              [lastName]="booking.booked_by.last_name"
              section="reservations/bookings">
            </app-customer-link>
          </div>
        </td>
        <td>
          <!-- Resolved once per row via `as`: it builds moment objects on
               every change-detection pass. -->
          <a *ngIf="getBookingEventDetailsLink(booking) as eventLink; else eventNameOnly"
             [routerLink]="eventLink">{{ booking.event_type_name }}</a>
          <ng-template #eventNameOnly>{{ booking.event_type_name }}</ng-template>
        </td>
        <td>
          <span *ngIf="booking.event_start_local_date" class="text-nowrap">
            {{ dateStringFromISO8601(booking.event_start_local_date) }}
          </span>
          <span *ngIf="!booking.event_start_local_date" class="text-muted">N/A</span>
        </td>
        <td>
          <!--
            Name the participants while there are few enough of them to be
            worth reading; past that the count is the useful thing (a private
            event can hold dozens).
          -->
          <ng-container *ngIf="allParticipantsAreNamed(booking)">
            <div *ngFor="let participant of booking.participants"
                 [class.reservation-cancelled]="participant.is_cancelled">
              <!--
                The participant's upcoming reservations: they are the attendee,
                so their reservations view is the relevant one.
              -->
              <app-customer-link *ngIf="participant.customer_id"
                [customerId]="participant.customer_id"
                [firstName]="participant.first_name"
                [lastName]="participant.last_name"
                section="reservations/upcoming">
              </app-customer-link>
              <span *ngIf="!participant.customer_id && getUnlinkedParticipantName(participant)">
                {{ getUnlinkedParticipantName(participant) }}
              </span>
              <!--
                A private event request's placeholder reservation has no name
                entered yet, and would otherwise render as an empty cell.
              -->
              <span *ngIf="!participant.customer_id && !getUnlinkedParticipantName(participant)"
                    class="text-muted">
                Details not entered
              </span>
            </div>
          </ng-container>
          <span *ngIf="!allParticipantsAreNamed(booking)" class="text-nowrap">
            {{ booking.num_participants }} participants
          </span>
          <span *ngIf="booking.num_participants === 0" class="text-muted">None</span>
        </td>
        <td>
          <!--
            A booking always carries an invoice, even with no fees at all, so a
            free booking would otherwise read as an unpaid one. The Upcoming
            view makes the same distinction via its fee/add-on fields.
          -->
          <app-payment-status-badge *ngIf="booking.has_payable_invoice"
                                    [status]="booking.payment_status"
                                    [invoiceId]="booking.invoice_id"></app-payment-status-badge>
          <span *ngIf="!booking.has_payable_invoice" class="text-muted">N/A</span>
        </td>
        <td>
          <span *ngIf="hasAmountDue(booking)" class="text-danger text-nowrap">
            {{ organizationConfigService.formatCurrency(booking.invoice_amount_due) }}
          </span>
          <span *ngIf="!hasAmountDue(booking)" class="text-muted">&mdash;</span>
        </td>
        <td class="table-button-cell">
          <button class="btn btn-sm btn-secondary table-12px-button float-right"
                  type="button"
                  (click)="openBookingDetailsModal(booking)">
            View
          </button>
        </td>
      </tr>
      <tr *ngIf="bookingsList.results.length === 0">
        <td colspan="99" class="text-muted">No bookings</td>
      </tr>
      </tbody>

      <tbody *ngIf="loadingBookings || !bookingsList">
      <tr>
        <td colspan="99" class="text-muted">Loading bookings...</td>
      </tr>
      </tbody>

    </table>
  </div>

  <app-pagination-row [(pageSize)]="pageSizeCustomerBookings"
                      [(pageNum)]="pageNumber"
                      [reloadSubject]="filtersChangedSubject"
                      [lastPageNum]="lastPageNumber()"></app-pagination-row>
</div>
